import React from "react"
import { Typography, Box, Divider } from "@mui/material"

const EmployerServiceAgreementV1 = () => {
  return (
    <Box sx={{ backgroundColor: "white", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 800, mx: "auto", px: { xs: 2, md: 4 }, py: 4 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
          All Pages Copyright © 2026, MyStethi, LLC, All Rights Reserved.
        </Typography>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "primary.dark" }}>
          EMPLOYER SERVICE AGREEMENT
        </Typography>

        <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, fontStyle: "italic" }}>
          Last Updated: May 27, 2026
        </Typography>

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>Effective Date</strong>: Upon account creation
        </Typography>

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          These Terms and Conditions ("Agreement") set forth the terms and conditions under which
          MyStethi, LLC ("MyStethi") will provide access to its proprietary technology to Subscriber.
          The Agreement sets forth the terms and conditions under which Subscriber may Use (as defined
          herein) MyStethi's platform located at https://mystethi.com/ ("Software"), solely in
          connection with provider recruitment.
        </Typography>

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          By creating an employer account and accepting this Agreement, Subscriber agrees to be bound
          by the terms below. This Agreement supplements the MyStethi Terms of Service and Privacy
          Policy.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          1. LICENSE GRANT AND RIGHT OF USE
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>1.1 License Grant.</strong> Subject to all limitations and restrictions contained herein,
          MyStethi grants Subscriber a subscription, software as a service (SaaS), nonexclusive and
          nontransferable right to use the Software as hosted by MyStethi, as described in the
          subscription plan selected at sign-up ("Use").
          <br /><br />
          <strong>1.2 Use.</strong> Subscriber shall not allow any website that is not fully owned by Subscriber
          to frame, syndicate, distribute, replicate, or copy any portion of Subscriber's web site
          that provides direct or indirect access to the Software.
          <br /><br />
          <strong>1.3 Additional Restrictions.</strong> In no event shall Subscriber disassemble, decompile, or
          reverse engineer the Software or Confidential Information (as defined herein) or permit
          others to do so. Disassembling, decompiling, and reverse engineering include, without
          limitation: (i) converting the Software from a machine-readable form into a human-readable
          form; (ii) disassembling or decompiling the Software by using any means or methods to
          translate machine-dependent or machine-independent object code into the original
          human-readable source code or any approximation thereof; (iii) examining the
          machine-readable object code that controls the Software's operation and creating the
          original source code or any approximation thereof by, for example, studying the Software's
          behavior in response to a variety of inputs; or (iv) performing any other activity related
          to the Software that could be construed to be reverse engineering, disassembling, or
          decompiling. To the extent any such activity may be permitted pursuant to written agreement,
          the results thereof shall be deemed Confidential Information subject to the requirements of
          this Agreement. Subscriber may use MyStethi's Confidential Information solely in connection
          with the Software and pursuant to the terms of this Agreement.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          2. PAYMENT
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>2.1 Fees.</strong> Subscriber shall pay MyStethi the fees associated with the subscription plan
          selected at sign-up ("Subscription Fees"). Unless otherwise provided, all fees shall be paid
          to MyStethi within fifteen (15) days of the date of invoice. The initial payment for the
          Software must be made on the subscription effective date. Any late payment shall be subject
          to any costs of collection (including reasonable legal fees) and shall bear interest at the
          rate of one and one-half percent (1.5%) per month (prorated for partial periods) or at the
          maximum rate permitted by law, whichever is less.
          <br /><br />
          <strong>2.2 Taxes.</strong> The license, service fees, and other amounts required to be paid hereunder do
          not include any amount for taxes or levy (including interest and penalties). Subscriber shall
          reimburse MyStethi and hold MyStethi harmless for all sales, use, VAT, excise, property or
          other taxes or levies which MyStethi is required to collect or remit to applicable tax
          authorities. This provision does not apply to MyStethi's income or franchise taxes, or any
          taxes for which Subscriber is exempt, provided Subscriber has furnished MyStethi with a valid
          tax exemption certificate.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          3. MAINTENANCE AND SUPPORT SERVICES
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>3.1 Maintenance.</strong> MyStethi shall use commercially reasonable efforts to provide
          corrections to reported problems that (i) prevent the Software from conforming in material
          respects to its specifications, and (ii) are replicated and diagnosed by MyStethi as defects
          in the Software ("Maintenance and Support Services"). MyStethi shall use commercially
          reasonable efforts to begin working on a resolution to Subscriber's written notice of
          reported problems within fourteen (14) days, provided corrections shall be prioritized in
          MyStethi's reasonable discretion. A response is not a guaranty of a solution to the reported
          problem; however MyStethi will keep Subscriber apprised of the resolution closure. Additional
          features and functions are not included as part of the maintenance and support services.
          <br /><br />
          <strong>3.2 Exclusions.</strong> MyStethi shall not be obligated to provide Maintenance and Support
          Services for any software other than the generally available Software delivered to Subscriber
          pursuant to this Agreement (collectively the "Unsupported Code"). Any MyStethi support
          services related to Unsupported Code shall be subject to execution of a mutually agreed upon
          assignment order issued under a professional services agreement.
          <br /><br />
          <strong>3.3 Third Parties.</strong> MyStethi shall have the right to use third parties, including
          employees of MyStethi's affiliates and subsidiaries ("Subcontractors") in performance of its
          obligations and services hereunder and, for purposes of this Section, all references to
          MyStethi or its employees shall be deemed to include such Subcontractors.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          4. OWNERSHIP
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>4.1 Reservation of Rights.</strong> By accepting this Agreement, Subscriber irrevocably
          acknowledges that, subject to the licenses granted herein, Subscriber has no ownership
          interest in the Software or MyStethi materials provided to Subscriber. MyStethi shall own all
          right, title, and interest in all such Software and MyStethi materials, subject to any
          limitations associated with intellectual property rights of third parties. MyStethi reserves
          all rights not specifically granted herein.
          <br /><br />
          <strong>4.2 Data.</strong> Subscriber shall own all data collected through the Software including, but
          not limited to, user data ("Subscriber Data"). MyStethi will not sell or disclose the
          Subscriber Data on an individual (non-aggregated) basis. MyStethi may aggregate de-identified
          Subscriber Data, including with the data of other MyStethi customers, and analyze such
          Subscriber Data and user behavior data including use of aggregate data to (i) help develop
          new features of the Software; (ii) recommend areas for examination or improvement; (iii)
          train algorithms and machine learning; (iv) improve conversational artificial intelligence;
          (v) analyze, compare, and benchmark Subscriber Data; and (vi) for any other legal purpose.
          To the extent that use of Subscriber Data and user behavior data gathered by MyStethi would
          require a license, Subscriber hereby automatically grants such royalty-free license to
          MyStethi.
          <br /><br />
          <strong>4.3 Tools.</strong> MyStethi reserves the right to select and use any technology and development
          tools, including but not limited to generative artificial intelligence ("AI") and machine
          learning models, at its sole discretion to assist in the performance of this Agreement.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          5. CONFIDENTIALITY
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>5.1 Definition.</strong> "Confidential Information" includes all information marked pursuant to
          this Section and disclosed by either party, before or after the Effective Date, and generally
          not publicly known, whether tangible or intangible and in whatever form or medium provided,
          as well as any information generated by a party that contains, reflects, or is derived from
          such information.
          <br /><br />
          <strong>5.2 Confidentiality of Software.</strong> All Confidential Information in tangible form shall be
          marked as "Confidential" or the like or, if intangible (e.g. orally disclosed), shall be
          designated as being confidential at the time of disclosure and shall be confirmed as such in
          writing within thirty (30) days of the initial disclosure. Notwithstanding the foregoing, the
          following is deemed MyStethi Confidential Information with or without such marking or written
          confirmation: (i) the Software and other related materials furnished by MyStethi; (ii) the
          oral and visual information relating to the Software; and the terms and conditions of this
          Agreement.
          <br /><br />
          <strong>5.3 Exceptions.</strong> Without granting any right or license, the obligations of the parties
          hereunder shall not apply to any material or information that: (i) is or becomes a part of
          the public domain through no act or omission by the receiving party; (ii) is independently
          developed by the other party without use of the disclosing party's Confidential Information;
          (iii) is rightfully obtained from a third party without any obligation of confidentiality; or
          (iv) is already known by the receiving party without any obligation of confidentiality prior
          to obtaining the Confidential Information from the disclosing party. In addition, neither
          party shall be liable for disclosure of Confidential Information if made in response to a
          valid order of a court or authorized agency of government, provided that notice is promptly
          given to the disclosing party so that the disclosing party may seek a protective order. The
          parties shall cooperate fully in seeking such protective order and in engaging in such other
          efforts to minimize the required disclosure.
          <br /><br />
          <strong>5.4 Ownership of Confidential Information.</strong> Nothing in this Agreement shall be construed
          to convey any title or ownership rights to the Software or other Confidential Information to
          Subscriber or to any patent, copyright, trademark, or trade secret embodied therein, or to
          grant any other right, title, or ownership interest to the MyStethi Confidential Information.
          Neither party shall, in whole or in part, sell, lease, license, assign, transfer, or disclose
          the Confidential Information to any third party and shall not copy, reproduce or distribute
          the Confidential Information except as expressly permitted in this Agreement. Each party
          shall take every reasonable precaution, but no less than those precautions used to protect
          its own Confidential Information, to prevent the theft, disclosure, and the unauthorized
          copying, reproduction or distribution of the Confidential Information.
          <br /><br />
          <strong>5.5 Non-Disclosure.</strong> Each party agrees at all times to keep strictly confidential all
          Confidential Information belonging to the other party. Each party agrees to restrict access
          to the other party's Confidential Information only to those employees or Subcontractors who
          (i) require access in the course of their assigned duties and responsibilities; and (ii) have
          agreed in writing to be bound by provisions no less restrictive than those set forth in this
          Section.
          <br /><br />
          <strong>5.6 Injunctive Relief.</strong> Each party acknowledges that any unauthorized disclosure or use
          of the Confidential Information would cause the other party imminent irreparable injury and
          that such party shall be entitled to, in addition to any other remedies available at law or
          in equity, temporary, preliminary, and permanent injunctive relief in the event the other
          party does not fulfill its obligations under this Section.
          <br /><br />
          <strong>5.7 Suggestions/Improvements to Software.</strong> Notwithstanding this Section, unless otherwise
          expressly agreed in writing, all suggestions, solutions, improvements, corrections, and
          other contributions provided by Subscriber regarding the Software or other MyStethi materials
          provided to Subscriber shall be owned by MyStethi, and Subscriber hereby agrees to assign any
          such rights to MyStethi. Nothing in this Agreement shall preclude MyStethi from using in any
          manner or for any purpose it deems necessary, the know-how, techniques, or procedures
          acquired or used by MyStethi in the performance of services hereunder.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          6. WARRANTY
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>6.1 Authorized Representative.</strong> Subscriber and MyStethi warrant that each has the right
          to enter into this Agreement and that the Agreement shall be executed by an authorized
          representative of each entity.
          <br /><br />
          <strong>6.2 Disclaimer of Warranties.</strong> Subscriber acknowledges and agrees that it is not relying
          on any statement or warranty not expressly provided herein with respect to the Software or
          maintenance, or other services provided hereunder. THE MATERIALS ACCESSABLE THROUGH THE
          SOFTWARE ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND EITHER EXPRESS OR IMPLIED.
          MYSTETHI SHALL HAVE NO RESPONSIBILITY OR LIABILITY FOR ANY DATA, CONTENT OR MATERIALS
          ACCESSED THROUGH THE SOFTWARE AND MYSTETHI MAKES NO REPRESENTATION THAT ANY DATA, CONTENT
          OR MATERIALS IS ACCURATE OR COMPLETE. MYSTETHI MAKES NO WARRANTIES, EXPRESS OR IMPLIED,
          INCLUDING EXPRESS OR IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NONINFRINGEMENT.
          <br /><br />
          <strong>6.3 No Modifications.</strong> Notwithstanding anything to the contrary in this Section, any and
          all warranties under this Agreement are VOID if Subscriber has made changes to the Software
          or has permitted any changes to be made other than by or with the express, written approval
          of MyStethi.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          7. LIMITATION OF LIABILITY
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>7.1 Liability Cap.</strong> IN NO EVENT SHALL MYSTETHI BE LIABLE UNDER ANY THEORY OF LIABILITY,
          WHETHER IN AN EQUITABLE, LEGAL, OR COMMON LAW ACTION ARISING HEREUNDER FOR CONTRACT, STRICT
          LIABILITY, INDEMNITY, TORT (INCLUDING NEGLIGENCE), OR OTHERWISE, FOR DAMAGES WHICH, IN THE
          AGGREGATE, EXCEED THE AMOUNT OF THE FEES PAID BY SUBSCRIBER FOR THE SOFTWARE WHICH GAVE
          RISE TO SUCH DAMAGES IN THE SIX (6) MONTHS PRIOR TO THE CLAIM AND NOTWITHSTANDING THE
          FAILURE OF ESSENTIAL PURPOSE OF ANY REMEDY.
          <br /><br />
          <strong>7.2 Disclaimer of Damages.</strong> IN NO EVENT SHALL MYSTETHI BE LIABLE FOR ANY SPECIAL,
          INCIDENTAL, INDIRECT, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES OF ANY KIND AND
          HOWEVER CAUSED INCLUDING, BUT NOT LIMITED TO, BUSINESS INTERRUPTION OR LOSS OF PROFITS,
          BUSINESS OPPORTUNITIES, OR GOODWILL EVEN IF NOTIFIED OF THE POSSIBILITY OF SUCH DAMAGE,
          AND NOTWITHSTANDING THE FAILURE OF ESSENTIAL PURPOSE OF ANY REMEDY.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          8. TERM AND TERMINATION
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>8.1 Termination by MyStethi.</strong> This Agreement and any license created hereunder may be
          terminated by MyStethi (i) if Subscriber fails to make any payments due hereunder within
          fifteen (15) days of the due date; (ii) on thirty (30) days written notice to Subscriber if
          Subscriber fails to perform any other material obligation required of it hereunder, and such
          failure is not cured within such thirty (30) day period; or (iii) Subscriber files a petition
          for bankruptcy or insolvency, has an involuntary petition filed against it, commences an
          action providing for relief under bankruptcy laws, files for the appointment of a receiver,
          or is adjudicated a bankrupt concern.
          <br /><br />
          <strong>8.2 Termination by Subscriber.</strong> This Agreement may be terminated by Subscriber on thirty
          (30) days written notice to MyStethi if MyStethi fails to perform any material obligation
          required of it hereunder, and such failure is not cured within thirty (30) days from
          MyStethi's receipt of Subscriber's notice or a longer period if MyStethi is working
          diligently towards a cure.
          <br /><br />
          <strong>8.3 Termination.</strong> Upon termination of this Agreement, Subscriber shall no longer access
          the Software and Subscriber shall not circumvent any security mechanisms contained therein.
          <br /><br />
          <strong>8.4 Other Remedies.</strong> Termination of this Agreement shall not limit either party from
          pursuing other remedies available to it, including injunctive relief, nor shall such
          termination relieve Subscriber's obligation to pay all fees that have accrued or are
          otherwise owed by Subscriber under this Agreement.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, fontWeight: 700 }}>
          9. MISCELLANEOUS
        </Typography>

        <Typography variant="body1" component="div" sx={{ mb: 1.5, lineHeight: 1.7 }}>
          <strong>9.1 Compliance With Laws.</strong> Subscriber agrees to comply with all applicable laws,
          regulations, and ordinances relating to its performance under this Agreement. The parties
          agree that the Agreement shall not be governed by the United Nations Convention on the
          International Sale of Goods or by UCITA, the application of which is expressly excluded.
          <br /><br />
          <strong>9.2 Assignment.</strong> Subscriber may not assign this Agreement or otherwise transfer any
          license created hereunder whether by operation of law, change of control, or in any other
          manner, without the prior written consent of MyStethi. Any assignment or transfer in
          violation of this Section shall be null and void.
          <br /><br />
          <strong>9.3 Survival.</strong> The provisions set forth in Sections 2, 4, 6.2, 7, 8.4, and 9 of this
          Agreement and any applicable license hereunder shall survive termination or expiration of
          this Agreement.
          <br /><br />
          <strong>9.4 Notices.</strong> Any notice required under this Agreement shall be given in writing and
          shall be deemed effective upon delivery to the party to whom addressed. All notices shall be
          sent to the applicable address associated with Subscriber's account or to such other address
          as the parties may designate. Unless otherwise specified, all notices to MyStethi shall be
          sent to the attention of the CEO. Any notice of material breach shall clearly define the
          breach including the specific contractual obligation that has been breached.
          <br /><br />
          <strong>9.5 Force Majeure.</strong> MyStethi shall not be liable to Subscriber for any delay or failure
          of MyStethi to perform its obligations hereunder if such delay or failure arises from any
          cause or causes beyond the reasonable control of MyStethi. Such causes shall include, but are
          not limited to, acts of God, floods, fires, loss of electricity or other utilities, or delays
          by Subscriber in providing required resources or support or performing any other requirements
          hereunder.
          <br /><br />
          <strong>9.6 Restricted Rights.</strong> Use of the Software by or for the United States Government is
          conditioned upon the Government agreeing that the Software is subject to Restricted Rights as
          provided under the provisions set forth in FAR 52.227-19. Subscriber shall be responsible for
          assuring that this provision is included in all agreements with the United States Government
          and that the Software, when delivered to the Government, is correctly marked as required by
          applicable Government regulations governing such Restricted Rights as of such delivery.
          <br /><br />
          <strong>9.7 Entire Agreement.</strong> This Agreement, together with the MyStethi Terms of Service and
          Privacy Policy, constitutes the entire agreement between the parties regarding the subject
          matter hereof and supersedes all proposals and prior discussions and writings between the
          parties with respect thereto.
          <br /><br />
          <strong>9.8 Modifications.</strong> The parties agree that this Agreement cannot be altered, amended or
          modified, except by a writing signed by an authorized representative of each party.
          <br /><br />
          <strong>9.9 Nonsolicitation.</strong> During the term of this Agreement and for a period of two (2) years
          thereafter, Subscriber agrees not to hire, solicit, or attempt to solicit, the services of
          any employee or Subcontractor of MyStethi without the prior written consent of MyStethi.
          Subscriber further agrees not to hire, solicit, nor attempt to solicit, the services of any
          former employee or Subcontractor of MyStethi for a period of one (1) year from such former
          employee's or Subcontractor's last date of service with MyStethi. Violation of this provision
          shall entitle MyStethi to liquidated damages against Subscriber equal to two hundred percent
          (200%) of the solicited person's gross annual compensation.
          <br /><br />
          <strong>9.10 Publicity.</strong> Subscriber agrees to cooperate with MyStethi (i) in preparation of at
          least one (1) press release, where the aforementioned materials can be used in/on MyStethi's
          Web site, marketing materials, trade shows, public advertisements, and other associated
          marketing uses ("MyStethi Marketing Materials"); and (ii) in preparation of a
          MyStethi-sponsored testimonial advertisement to be published on digital or printed media
          platforms, including but not limited to online news outlets, industry blogs, social media
          channels, and other digital or printed publications as well as for use in MyStethi Marketing
          Materials. The parties further agree that MyStethi may include Subscriber's logo on publicly
          displayed customer lists (including MyStethi's Internet Web site and public advertisements).
          <br /><br />
          <strong>9.11 No Waiver.</strong> No failure or delay in enforcing any right or exercising any remedy will
          be deemed a waiver of any right or remedy.
          <br /><br />
          <strong>9.12 Severability and Reformation.</strong> Each provision of this Agreement is a separately
          enforceable provision. If any provision of this Agreement is determined to be or becomes
          unenforceable or illegal, such provision shall be reformed to the minimum extent necessary in
          order for this Agreement to remain in effect in accordance with its terms as modified by such
          reformation.
          <br /><br />
          <strong>9.13 Choice of Law.</strong> This Agreement shall be governed and interpreted by the laws of the
          state of Texas without regard to the conflicts of law provisions of any state or
          jurisdiction. Any action related to this Agreement shall be brought in the state or federal
          courts located in Austin, Texas and Subscriber hereby submits to the exclusive jurisdiction
          of such courts.
        </Typography>
      </Box>
    </Box>
  )
}

export default EmployerServiceAgreementV1
